// The routing wire shapes. `SourceRow` is the columns of A2000_Gold `dbo.vw_routing_open_pick_tickets`
// (lowercase) that the pages read — not ORDER_NO (the same value as `customer_po`) nor the line count,
// as the Worklist sends every row; a Deposco SO takes the same shape, its own columns in `deposco`
// (`deposco.ts`).

import type {
  CARRIER_LIST,
  ChangeAction,
  ListName,
  PickupStatus,
  ROUTING_STATUS_LIST,
  ShippingRowStatus,
} from "../entities";
import type { DeposcoBrief, DeposcoInfo, DeposcoRow, LineSource } from "./deposco";
import type { ExcelHistoryRow } from "./excel";
import type { PeField, PeStored } from "./pe-order";
import type { RuleBase } from "./routing-rules";

/** One open pick ticket, as the view gives it — or one open Deposco SO. Dates are `YYYY-MM-DD`. */
export interface SourceRow {
  /** The A2000 pick ticket number; a Deposco SO's SO # (SO230792). */
  pick_ticket: number | string;
  wh: string | null;
  /** The A2000 order # (CTRL_NO) it was made from: what a pre-route is typed with. */
  ctrl_no: string | null;
  corp: string | null;
  customer_code: string | null;
  customer_name: string | null;
  div: string | null;
  division: string | null;
  store: string | null;
  /** The customer PO: A2000 ORDER_NO (the view's `order_no` too), always filled. */
  customer_po: string | null;
  pt_date: string | null;
  start_date: string | null;
  cancel_date: string | null;
  qty: number | null;
  wms_shipment: string | null;
  in_wms: 0 | 1;
  routing_open_bdays: number | null;
  routing_due_bdays: number | null;
  source_loaded_at: string | null;
  /** The DC it ships to, where A2000 has one (8 customers on 2026-10-20); null otherwise. */
  dc_no: string | null;
  dc_name: string | null;
  /** A Deposco SO: its own columns. Absent on an A2000 pick ticket. */
  deposco?: DeposcoInfo;
}

/** The warehouse's fields on a line — the editable cells. */
export const WAREHOUSE_FIELDS = [
  "readyDate",
  "workNeeded",
  "productionNotes",
  "routingStatus",
  "routingNotes",
  "routingRef",
  "schedShip",
  "appointmentDate",
  "inventoryNotes",
  "lead",
  "timing",
  "notes",
  "routedOn",
  // The PE ORDERS columns (`pe-order.ts`), all text here; stored as 017 says (`peStored`).
  "peShipDate",
  "pullGroup",
  "locQty",
  "peAssignedTo",
  "peEstimate",
  "peStatus",
  "peStartAt",
  "peEndAt",
  "pallets",
  "peComments",
] as const;
export type WarehouseField = (typeof WAREHOUSE_FIELDS)[number];

/** Routing status is a value of its managed list: its category is on the line (`LineView.routingCategory`). */
export type WarehouseValues = Record<WarehouseField, string>;

/** Stored: Shipped, Cancelled. "Regrouped by WMS" is read, never stored: a line from before WMS grouping. */
export type FinishedStatus = "Shipped" | "Cancelled" | "Regrouped by WMS";

/** A `routing-line` record as the store lists it (no `rv`: the list has none). The PE fields as 017 stores them. */
export interface RoutingLineRecord extends Omit<WarehouseValues, PeField>, Partial<PeStored> {
  id: string;
  groupKey: string;
  /** "By hand" only on a line from before WMS grouping. */
  grouping: "Automatic" | "By hand";
  groupedBy: string;
  groupedAt: string;
  reviewStatus: "Not reviewed" | "Approved" | "Split";
  reviewedBy: string;
  reviewedAt: string;
  finishedStatus: Exclude<FinishedStatus, "Regrouped by WMS"> | null;
  finishedAt: string | null;
  finishedBy: string;
  finishedNote: string;
  /** The pick tickets as last approved, one text over three fields (`baseline.ts`); "" for none. */
  baseline1: string;
  baseline2: string;
  baseline3: string;
  /** The WMS # (V7 step 2); "" on a line from before WMS grouping. */
  wmsNo: string;
  /** What carry-overs left undecided: JSON (`carry.ts`); "" for none. */
  carryConflicts: string;
  /** The lines and WMS #s a carry-over brought entries from, as text (`carry.ts`). */
  carriedFrom: string;
  /** READY DATE and SCHEDULED SHIP DATE as last approved (014); null: blank then. */
  approvedReadyDate?: string | null;
  approvedSchedShip?: string | null;
  updated_at?: string;
}

/** A `line-pick-ticket` record: the copy of a pick ticket's source fields as last approved. */
export interface LinePickTicketRecord {
  id: string;
  lineId: string;
  pickTicket: string;
  wh: string;
  orderNo: string;
  corp: string;
  customerCode: string;
  customerName: string;
  div: string;
  division: string;
  store: string;
  customerPo: string;
  ptDate: string | null;
  startDate: string | null;
  cancelDate: string | null;
  qty: number | null;
  wmsShipment: string;
  routingOpenBdays: number | null;
  routingDueBdays: number | null;
  sourceLoadedAt: string;
}

/** Every managed list: the line fields' three, Carrier and Routing status. */
export type AnyListName = ListName | typeof CARRIER_LIST | typeof ROUTING_STATUS_LIST;

export interface ListValueRecord {
  id: string;
  list: AnyListName;
  value: string;
  retired: "No" | "Yes";
  /** Routing status only: what it means to the rules (`routing-status.ts`). */
  category?: string | null;
}

/** A `division-abbrev` record: a division's short name, as someone saved it (Admin → Divisions). */
/** A customer's shown name (Admin → Customers, `customer-names.ts`): one per source + code. */
export interface CustomerNameRecord {
  id: string;
  source: LineSource;
  /** A2000 CUSTOMER, or a Deposco trading partner's code. */
  code: string;
  /** The original name when saved: the log's words, and a code gone from the views. */
  customerName: string;
  /** "" is the default: A2000 its code, Deposco its name. */
  shownName: string;
  updatedBy: string;
  updated_at?: string;
}

/**
 * A customer's routing rule (Admin → Routing rules, `routing-rules.ts`): one per source + code.
 * `noRule` is "No routing rule" set in the app; `separate` keeps the code out of its shown-name group;
 * empty days are "no app days". No record is the fallback. The flags are BIT: true / false as read.
 */
export interface RoutingRuleRecord {
  id: string;
  source: LineSource;
  code: string;
  /** The original name when saved: the log's words, and a code gone from the views. */
  customerName: string;
  /** Window opens: business days before FIRST SHIP DATE, 0–60. */
  openBdays: number | null;
  /** Route by: business days before CANCEL DATE, 0–60. */
  dueBdays: number | null;
  noRule: boolean | number;
  separate: boolean | number;
  notes: string;
  updatedBy: string;
  updated_at?: string;
}

export interface DivisionAbbrevRecord {
  id: string;
  corp: string;
  div: string;
  /** The full name when saved: finds the abbreviation where only the name is known (a workbook row). */
  division: string;
  /** "" is the default, the first 7 characters of the name (`divisions.ts`). */
  abbreviation: string;
  updatedBy: string;
  updated_at?: string;
}

/** A `customer-assignment` record (`entities.ts`): a label of customers and who looks after it. */
export interface CustomerAssignmentRecord {
  id: string;
  /** Lower-case. */
  personEmail: string;
  personName: string;
  label: string;
  /** `<source>:<code>` keys, "; " between them; "" for none yet (Unmatched). */
  customerKeys: string;
  role: "Owner" | "Backup";
  note: string;
  retired: "No" | "Yes";
  updatedBy: string;
  rv?: string;
  updated_at?: string;
}

/** An `app-user` record (`entities.ts`): someone who opened the routing pages. */
export interface AppUserRecord {
  id: string;
  /** Lower-case, as sign-in gives it. */
  email: string;
  displayName: string;
  /** ISO times. */
  firstSeen: string;
  lastSeen: string;
  rv?: string;
}

/** A `user-preference` record (`entities.ts`): a person's default view of a page. */
export interface UserPreferenceRecord {
  id: string;
  personEmail: string;
  page: "worklist" | "history";
  /** JSON of the v.* keys (`viewToAddress`); "" for none. */
  view: string;
  /** JSON of [{ id, desc }]; "" for none. */
  sort: string;
  updatedAt: string;
  rv?: string;
}

/** A `pickup` record (`entities.ts`): one truck. */
export interface PickupRecord {
  id: string;
  wh: string;
  pickupDate: string | null;
  appTime: string;
  carrier: string;
  /** Picked from the carriers list (`carriers.ts`); empty on one typed before 013. */
  carrierCode?: string | null;
  carrierSource?: LineSource | null;
  door: string;
  loadRef: string;
  notes: string;
  status: PickupStatus;
  created_at?: string;
  updated_at?: string;
}

/** A `shipping-row` record: one line on a pickup. */
export interface ShippingRowRecord {
  id: string;
  pickupId: string;
  lineId: string;
  wmsNo: string;
  ctns: number | null;
  plts: number | null;
  loader: string;
  inTime: string;
  outTime: string;
  trailer: string;
  wmsAsnTrip: string;
  notes: string;
  sortOrder: number | null;
  status: ShippingRowStatus;
  created_at?: string;
  updated_at?: string;
}

/** A `shipment` record: one shipped line. */
export interface ShipmentRecord {
  id: string;
  lineId: string;
  pickupId: string;
  shipDate: string | null;
  carrier: string;
  /** Picked from the carriers list (`carriers.ts`); empty on one typed before 013. */
  carrierCode?: string | null;
  carrierSource?: LineSource | null;
  loadRef: string;
  tracking: string;
  loader: string;
  trailer: string;
  door: string;
  notes: string;
  ctns: number | null;
  plts: number | null;
  inTime: string;
  outTime: string;
  created_at?: string;
  updated_at?: string;
}

/** A `change-entry` record: one save, as the System log reads it (`change-log.ts`). */
export interface ChangeEntryRecord {
  id: string;
  entity: string;
  recordId: string;
  lineId: string;
  action: ChangeAction;
  changes: string;
  note: string;
  lineText: string;
  personId: string;
  personName: string;
  appVersion: string;
  /** ISO, the browser's clock at the save; "" on entries written before 009. */
  savedAt: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * One pick ticket's WMS shipment, as A2000_Gold `dbo.vw_routing_pick_ticket_shipments` gives it
 * (dataset `routing-pick-ticket-shipments`). Days are `YYYY-MM-DD`. The pages get the rows of
 * their lines' pick tickets that are SHIPPED or BILLED only.
 */
export interface PtShipmentRow {
  pick_ticket: number;
  wms_shipment: string | null;
  wms_status: string | null;
  wh: string | null;
  customer_code: string | null;
  order_no: string | null;
  store: string | null;
  dc_no: string | null;
  pt_date: string | null;
  date_shipped: string | null;
  ship_via: string | null;
  carrier_name: string | null;
  scac: string | null;
  bol_no: string | null;
  vics_bol_no: string | null;
  master_bol_no: string | null;
  appointment_id: string | null;
  appointment_date: string | null;
  eta_date: string | null;
  trailer_no: string | null;
  trailer_seal: string | null;
  tracking_no: string | null;
  pro_no: string | null;
  cartons: number | null;
  qty: number | null;
  weight: number | null;
  wh_ship_ref1: string | null;
  wh_ship_ref2: string | null;
  invoice_no: string | null;
}

/** The shipping entities, as one read of the store gives them. */
export interface StoredShipping {
  pickups: PickupRecord[];
  shippingRows: ShippingRowRecord[];
  shipments: ShipmentRecord[];
}

/** `GET /api/routing/worklist`, `/shipping` and `/history`. */
export interface RoutingPayload extends StoredShipping {
  source: SourceRow[];
  lines: RoutingLineRecord[];
  pickTickets: LinePickTicketRecord[];
  listValues: ListValueRecord[];
  /** The saved division abbreviations (a few dozen): every page shows a division by its own. */
  divisionAbbrevs: DivisionAbbrevRecord[];
  /** The saved customers' shown names: every page shows a customer by its own (`customer-names.ts`). */
  customerNames: CustomerNameRecord[];
  /** The saved routing rules (Admin → Routing rules): every page's windows count with them (`routing-rules.ts`). */
  routingRules: RoutingRuleRecord[];
  /** A2000's CUST_REF1/2 the windows fall back on: the whole open source's and the customer master's. */
  ruleBase: RuleBase;
  /** The A2000 shipment of each pick ticket of the page's lines that is SHIPPED or BILLED. */
  ptShipments: PtShipmentRow[];
  /** The Deposco SOs the page's lines hold that are no longer open (Ship Complete, Canceled): how they left. */
  deposcoClosed: DeposcoRow[];
  /** `/worklist` only, else null: the open Deposco SOs with a DC and no group yet — not lines. */
  deposcoWaiting: DeposcoBrief[] | null;
  /** The last change among the B2B orders the app reads (ISO): their newest `source_loaded_at`. */
  deposcoLoadedAt: string | null;
  /** When the Deposco job last ran (ISO), changed orders or not (`SourceVersion`); null when unread. */
  deposcoCheckedAt: string | null;
  /** Whether this person sees the $ value and manages the lists: the Manager Role. */
  manager: boolean;
  /** Whether this person may change anything (`routingWrite`): the Warehouse and Manager Roles, never Viewer. */
  canWrite: boolean;
  /**
   * Managers only, else null: pick ticket → its $ value, from the dataset
   * `routing-pick-ticket-values` (feature `routingManager`), read only for a manager.
   */
  values: Record<string, number> | null;
  /** `/history` only, else null: the old workbook's rows (dataset `routing-history-excel`). */
  excel: ExcelHistoryRow[] | null;
  /** `APP_ENV=local`. */
  local: boolean;
  /** `/worklist` while `ROUTING_IMPORT` is on, else null: Admin → Import's runs (`importedChipShown`). */
  importRuns: number | null;
  /** When the source was last loaded (ISO). */
  sourceLoadedAt: string | null;
}

/** `GET /api/routing/lists-config`: the starting values, `config/routing-lists.json`. */
export type RoutingListsConfig = Record<ListName, readonly string[]>;

/** Production Notes is free text; these are only suggestions (spec §3.5). */
export const PRODUCTION_NOTE_SUGGESTIONS = [
  "READY",
  "RELEASED",
  "PRINTING",
  "PRINTED",
  "IN PRODUCTION",
  "PARTIALLY STAGED",
  "STAGED",
  "PICK COMPLETE",
  "BACK ORDER",
  "SHIPPED",
  "CANCELLED",
] as const;

export const ROUTING_API = "/api/routing";
